import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { JobsPanelState, JobsStatus, jobStatusToString } from "../../../constants/Jobs";
import { FeatureTableContext, JobContext, UserContext, VersionContext } from "../../../Context";
import { triggerAlertsRefresh } from "../../../store/triggersSlice";
import {
    applyValidationResults,
    createNewJobStep,
    exportJobFeaturesWithChangesToSksd,
    getJobRelatedDocumentation,
    getJobRelatedObjects,
    hideDocumentationPoints,
    objectsShouldValidate,
    showDocumentationPoints,
    updateFeatureNfsStatus,
    updateJobStepComment,
} from "../../../utils/jobsUtils";
import InputField from "../../genericComponents/InputField/InputField";
import NfsButton from "../../genericComponents/NfsButton/NfsButton";
import "./JobDetails.css";
import ApproveIcon from "../../../assets/jobs/approve.svg?react";
import CheckPinIcon from "../../../assets/jobs/checkPin.svg?react";
import RejectIcon from "../../../assets/jobs/reject.svg?react";
import { formatDate } from "@arcgis/core/intl";
import { StoreState } from "../../../store/rootReducer";
import { getLayerName } from "../../../utils/arcgisUtils";
import { isDefaultVersion } from "../../../utils/versioningUtils";
import ConfirmationPopover from "../../genericComponents/confirmationPopover/confirmationPopover";
import { FeatureWithChanges, findFeaturesWithChangesFromJob } from "../../../utils/newVersioningUtils";
import EditedFeaturesForOperativeCompare from "../EditedFeatures/EditedFeaturesForOperativeCompare";
import Collapsible from "../../genericComponents/Collapsible/Collapsible";
import { NFSStatus } from "../../../utils/nfsUtils";
import type Graphic from "@arcgis/core/Graphic";
import type { ResourceHandle } from "@arcgis/core/core/Handles";

interface JobQA2DetailsProps {
    jobObject: Graphic;
    setJobPanelState: (state: JobsPanelState) => void;
    setLoading: (loading: boolean) => void;
    storeHighlightHandle: (handle: ResourceHandle | null) => void;
    clearHighlightedFeatures: () => void;
}

const JobQA2Details: React.FC<JobQA2DetailsProps> = ({
    jobObject,
    setJobPanelState,
    setLoading,
    storeHighlightHandle,
    clearHighlightedFeatures,
}) => {
    const dispatch = useDispatch();

    const [currentJobStep, setCurrentJobStep] = useState<Graphic | null>(null);
    const [previousJobStep, setPreviousJobStep] = useState<Graphic | null>(null);
    const [historicalSteps, setHistoricalSteps] = useState<Graphic[]>([]);

    const [jobFeaturesWithChanges, setJobFeaturesWithChanges] = useState<FeatureWithChanges[]>([]);
    const [isLoadingChanges, setIsLoadingChanges] = useState<boolean>(true);
    const [canIReviewJob, setCanIReviewJob] = useState<boolean>(false);

    const [comment, setComment] = useState<string | undefined>(undefined);

    const [documentation, setDocumentation] = useState<Graphic[]>([]);
    const [selectedDocumentation, setSelectedDocumentation] = useState<Graphic | null>(null);

    const activeVersion = useSelector((state: StoreState) => state.version.versionInfo);

    const versionContext = useContext(VersionContext);
    const versionManagementRef = versionContext?.versionServiceRef.value;

    const userContext = useContext(UserContext);
    const user = userContext?.user.value;

    const jobsContext = useContext(JobContext);
    const featureTableContext = useContext(FeatureTableContext);

    const returnToJobList = () => {
        setJobPanelState(JobsPanelState.LIST);
        jobsContext?.selectedJob.set(null);
    };

    const checkIfDefaultVersion = () => {
        if (versionManagementRef?.current && activeVersion) {
            return isDefaultVersion(activeVersion, versionManagementRef.current);
        }
    };

    const exportToSksd = async (): Promise<boolean> => {
        const allowOperativ = jobFeaturesWithChanges.some(
            (f) => f.mainFeature.defaultGraphic?.attributes.nfs_status === NFSStatus.OPERATIVE // simple jobs do not change status from Operative
        );
        const result = await exportJobFeaturesWithChangesToSksd(jobFeaturesWithChanges, allowOperativ);
        if (result.failedFeatures.length > 0) {
            result.failedFeatures.forEach((f) => {
                toast.error(`Feil ved eksport til SKSD: ${f.message}`, {
                    autoClose: false,
                    className: "toast-error",
                });
            });
            return false;
        } else {
            if (result.skippedFeatures.length > 0) {
                toast.info(
                    <>
                        {`Følgende objekter ble ikke eksportert til SKSD`}
                        <br />
                        <br />
                        {result.skippedFeatures.map((skipped) => (
                            <React.Fragment key={skipped.reason}>
                                {`${skipped.reason}:`}
                                <br />
                                {skipped.objectNames.map((name) => (
                                    <React.Fragment key={name}>
                                        {name}
                                        <br />
                                    </React.Fragment>
                                ))}
                                <br />
                            </React.Fragment>
                        ))}
                    </>,
                    {
                        autoClose: false,
                    }
                );
            }

            toast.success("Alle gyldige objekter i jobben har blitt eksportert til SKSD");

            return true;
        }
    };

    const approveQA2Job = async () => {
        try {
            setLoading(true);
            if (jobObject) {
                const success = await updateJobStepComment(
                    currentJobStep?.attributes.globalid,
                    comment ?? "",
                    user?.username ?? ""
                );
                if (success) {
                    const nfsStatusSuccess = await updateFeatureNfsStatus(jobFeaturesWithChanges);
                    if (!nfsStatusSuccess) {
                        toast.error(
                            "Kunne ikke oppdatere status på objektene i jobben. Jobben er ikke godkjent. Prøv igjen.",
                            { autoClose: false, className: "toast-error" }
                        );
                        return;
                    }
                    const sksdSuccess = await exportToSksd();
                    const newJobStatus = sksdSuccess ? JobsStatus.Ferdig : JobsStatus.FailedSendingToSksd;
                    const jobStepGlobalId = await createNewJobStep(jobObject.attributes.globalid, newJobStatus);
                    if (jobStepGlobalId) {
                        if (sksdSuccess) {
                            toast.success("Jobben er godkjent og satt til ferdig");
                        } else {
                            toast.error("Eksport til SKSD feilet. Jobben satt til Feil ved sending til SKSD.", {
                                autoClose: false,
                                className: "toast-error",
                            });
                        }
                        dispatch(triggerAlertsRefresh());
                        returnToJobList();
                    }
                }
            }
        } catch (error) {
            console.error("Error approving QA2 job:", error);
            toast.error("En feil oppsto ved godkjenning av jobben.");
        } finally {
            setLoading(false);
        }
    };

    const rejectQA2Job = async () => {
        setLoading(true);
        if (jobObject) {
            const success = await updateJobStepComment(
                currentJobStep?.attributes.globalid,
                comment ?? "",
                user?.username ?? ""
            );
            if (success) {
                const jobStepGlobalId = await createNewJobStep(
                    jobObject.attributes.globalid,
                    JobsStatus.JobbIkkeGodkjent
                );
                if (jobStepGlobalId) {
                    // props.resetJobsData();
                    toast.info("Jobben er markert som ikke godkjent");
                    returnToJobList();
                    dispatch(triggerAlertsRefresh());
                }
            }
        }
        setLoading(false);
    };

    const canIReview = () => {
        if (
            [
                JobsStatus.QA1,
                JobsStatus.JobbIkkeGodkjent,
                JobsStatus.PlanIkkeGodkjent,
                JobsStatus.Ferdig,
                JobsStatus.Utføres,
                JobsStatus.Utført,
                JobsStatus.TilUtføring,
                JobsStatus.TilKontroll,
                JobsStatus.Ukjent,
            ].includes(jobObject.attributes.status)
        ) {
            return false;
        }

        if (previousJobStep === null) {
            return false;
        }
        if (!user) {
            return false;
        }
        if (jobFeaturesWithChanges.length === 0) {
            return false;
        }

        if (jobObject.attributes.status === JobsStatus.QA2 && checkIfDefaultVersion()) {
            if (previousJobStep?.attributes.fullfort_av === user?.username) {
                // check if objects connected to job is layer 0 and klasse 3 or 4
                // or if layer 7 or 8
                // For these layers/klasses, the QA2 reviewer must be different from the user who completed the last step
                for (let featureWithChange of jobFeaturesWithChanges) {
                    const mainFeature = featureWithChange.mainFeature;
                    const layerId = mainFeature.layerId;
                    const klasse = mainFeature.versionGraphic?.attributes["klasse"];
                    if ((layerId === 0 && (klasse === 3 || klasse === 4)) || layerId === 7 || layerId === 8) {
                        return false;
                    }
                }
                return true;
            } else {
                return true;
            }
        } else {
            return false;
        }
    };

    const validateObjects = objectsShouldValidate(jobObject.attributes.jobb_type);

    const getValidationResults = async (objects: FeatureWithChanges[]) => {
        const versionName = activeVersion?.versionIdentifier.name ?? null;
        await applyValidationResults(objects, setJobFeaturesWithChanges, versionName, "SendToSKSD");
    };

    const getObjectsConnectedToJob = async () => {
        const job = jobObject;
        const versionName = activeVersion?.versionIdentifier.name;

        if (job && versionName) {
            setIsLoadingChanges(true);
            const objects = await findFeaturesWithChangesFromJob(job, versionName);
            console.log("Objects with changes from job:", objects);
            setJobFeaturesWithChanges(objects);
            setIsLoadingChanges(false);
            if (validateObjects) {
                getValidationResults(objects); // fire and forget
            }
        }
    };

    const getDocumentationSeamarkNumber = (graphic: Graphic) => {
        const documentationRelatedObjectGlobalId = graphic.attributes.nautisk_objekt_globalid;
        const documentationRelatedLayerId = graphic.attributes.nautisk_objekt_kilde;
        let documentationRelatedFeature: Graphic | undefined;
        for (let featureWithChange of jobFeaturesWithChanges) {
            if (
                featureWithChange.mainFeature.layerId === documentationRelatedLayerId &&
                featureWithChange.mainFeature.versionGraphic?.attributes.globalid === documentationRelatedObjectGlobalId
            ) {
                documentationRelatedFeature = featureWithChange.mainFeature.versionGraphic;
                break;
            }
            for (let relatedFeature of featureWithChange.relatedFeatures) {
                if (
                    relatedFeature.layerId === documentationRelatedLayerId &&
                    relatedFeature.versionGraphic?.attributes.globalid === documentationRelatedObjectGlobalId
                ) {
                    documentationRelatedFeature = relatedFeature.versionGraphic;
                    break;
                }
            }
            if (documentationRelatedFeature) {
                break;
            }
        }

        return (
            documentationRelatedFeature?.attributes["sjomerke_nr"] ??
            documentationRelatedFeature?.attributes["navn"] ??
            documentationRelatedFeature?.attributes["objectid"] + getLayerName(documentationRelatedLayerId)
        );
    };

    useEffect(() => {
        getJobRelatedObjects(jobObject.attributes.objectid, "Steg").then((objects) => {
            // Find step before this step
            const orderedSteps = [...objects];
            // Sorts steps by opprettet_dato in descending order in case there are multiple steps with the same jobb_status
            orderedSteps.sort((a, b) => b.attributes.opprettet_dato - a.attributes.opprettet_dato);

            let activeStep;
            for (let step of orderedSteps) {
                if (step.attributes.jobb_status === jobObject.attributes.status) {
                    activeStep = step;
                    break;
                }
            }
            if (activeStep) {
                setHistoricalSteps(() => {
                    return orderedSteps.filter((step) => step.attributes.globalid !== activeStep.attributes.globalid);
                });

                setCurrentJobStep(activeStep);
                // Find step with highest opprettet_dato less than or equal to activeStep
                // but with a different status (could be same timestamp but different status)
                const maxTime = activeStep.attributes.opprettet_dato;
                let previousStep = null;

                // Sort steps by opprettet_dato in descending order

                for (let step of orderedSteps) {
                    if (
                        step.attributes.globalid !== activeStep.attributes.globalid &&
                        step.attributes.opprettet_dato <= maxTime &&
                        step.attributes.jobb_status !== activeStep.attributes.jobb_status
                    ) {
                        previousStep = step;
                        break;
                    }
                }
                setPreviousJobStep(previousStep);
            }
        });
        getJobRelatedDocumentation(jobObject.attributes.objectid).then((docs) => {
            setDocumentation(docs);
        });
    }, [jobObject]);

    useEffect(() => {
        if (selectedDocumentation) {
            showDocumentationPoints(selectedDocumentation).then((layer) => {
                if (layer) {
                    if (!layer.loaded) {
                        layer.load().then(() => {
                            featureTableContext?.isOpen.set(true);
                            featureTableContext?.activeLayer.set(layer);
                        });
                    } else {
                        featureTableContext?.isOpen.set(true);
                        featureTableContext?.activeLayer.set(layer);
                    }
                }
            });
        }
    }, [selectedDocumentation]);

    useEffect(() => {
        if (jobFeaturesWithChanges.length > 0) {
            const canReview = canIReview();
            setCanIReviewJob(canReview);
        } else {
            setCanIReviewJob(false);
        }
    }, [jobFeaturesWithChanges, user, previousJobStep]);

    useEffect(() => {
        getObjectsConnectedToJob();
    }, []);

    useEffect(() => {
        return () => {
            featureTableContext?.activeLayer.set(undefined);
            featureTableContext?.isOpen.set(false);
            hideDocumentationPoints();
        };
    }, []);

    return (
        <>
            <div className="job-details-body">
                <div className="job-details-body-content">
                    <label className="job-details-label">
                        <span className="hidden">{jobObject.attributes.globalid}</span>
                        <span>Jobbnavn</span>
                        <span>{jobObject.attributes.navn}</span>
                    </label>
                    <label className="job-details-label">
                        <span>Beskrivelse</span>
                        <span>{jobObject.attributes.beskrivelse}</span>
                    </label>
                    {canIReviewJob && checkIfDefaultVersion() && (
                        <InputField
                            label="Tilbakemelding / kommentar"
                            onChange={setComment}
                            inputFieldId={jobObject.attributes.globalid + "comment"}
                            value={comment}
                            type="text"
                            isTextArea
                            textAreaRows={3}
                            maxLength={255}
                        />
                    )}
                    {historicalSteps.length > 0 && (
                        <Collapsible
                            isOpen={true}
                            setIsOpen={() => {}}
                            title={`Steg med kommentarer (${historicalSteps.length})`}
                            titleClassName="input-field-label"
                        >
                            {historicalSteps.map((step) => (
                                <label key={step.attributes.globalid} className="job-step-container">
                                    <div className="job-step-header">
                                        <span>
                                            <strong>{jobStatusToString(step.attributes.jobb_status)}</strong>
                                        </span>
                                        <span>
                                            {step.attributes.fullfort_av} ({formatDate(step.attributes.oppdatert_dato)})
                                        </span>
                                    </div>
                                    <span>{step.attributes.kommentar ?? "[ingen kommentar]"}</span>
                                </label>
                            ))}
                        </Collapsible>
                    )}

                    <div className="empty-button flex-row small-gap center">
                        <CheckPinIcon />
                        {`Objekter tilknyttet jobben (${jobFeaturesWithChanges.length})`}
                    </div>
                    <div className="flex-row space-between">
                        <span className="job-card-key">Eier</span>
                        <span className="job-card-value bold">{jobObject.attributes.opprettet_av}</span>
                    </div>
                    <div className="flex-row space-between">
                        <span className="job-card-key">Oppdatert</span>
                        <span className="job-card-value bold">{formatDate(jobObject.attributes.oppdatert_dato)}</span>
                    </div>
                    {documentation.length > 0 && (
                        <Collapsible
                            isOpen={true}
                            setIsOpen={() => {}}
                            title={`Dokumentasjon (${documentation.length})`}
                            titleClassName="input-field-label"
                        >
                            {documentation.map((doc) => {
                                return (
                                    <div key={doc.attributes.globalid} className="job-details-documentation-item">
                                        <NfsButton
                                            outlined
                                            className={`w-100 job-details-documentation-button ${
                                                doc === selectedDocumentation ? "selected-documentation" : ""
                                            }`}
                                            onClick={() => {
                                                setSelectedDocumentation(doc);
                                            }}
                                        >
                                            <div className="job-details-documentation-item-header">
                                                <span>
                                                    {getDocumentationSeamarkNumber(doc) +
                                                        " " +
                                                        (doc.attributes.beskrivelse ?? "")}
                                                </span>
                                                <span className="job-details-documentation-item-explanation">
                                                    {doc.attributes.forklaring ?? ""}
                                                </span>
                                            </div>
                                        </NfsButton>
                                    </div>
                                );
                            })}
                        </Collapsible>
                    )}
                </div>
                <EditedFeaturesForOperativeCompare
                    featuresAddedToJob={jobFeaturesWithChanges}
                    isLoadingChanges={isLoadingChanges}
                    storeHighlightHandle={storeHighlightHandle}
                    clearHighlightedFeatures={clearHighlightedFeatures}
                    jobStatus={jobObject.attributes.status}
                />
            </div>

            {canIReviewJob && jobObject.attributes.status === JobsStatus.QA2 && (
                <div className="job-details-actions">
                    <div className="job-details-actions-buttons">
                        <NfsButton
                            title={
                                !comment || comment.length === 0
                                    ? "Kan ikke avslå uten kommentar"
                                    : "Avslå denne jobben"
                            }
                            outlined
                            disabled={!comment || comment.length === 0}
                            onClick={rejectQA2Job}
                        >
                            <RejectIcon />
                            Avslå
                        </NfsButton>
                        <ConfirmationPopover
                            message="Bekreft at du ønsker å godkjenne jobben"
                            onConfirm={approveQA2Job}
                            title="Godkjenn jobb"
                            confirmText="Bekreft"
                            cancelText="Avbryt"
                            onCancel={() => {}}
                            buttonStyle={{ width: "50%" }}
                            disabled={isLoadingChanges}
                        >
                            <div className="nfs-button">
                                <ApproveIcon />
                                Godta
                            </div>
                        </ConfirmationPopover>
                    </div>
                </div>
            )}
            {!checkIfDefaultVersion() && (
                <div className="job-details-actions">
                    <span>For å sikre datakvalitet må jobben godkjennes fra Planroot</span>
                </div>
            )}
        </>
    );
};

export default JobQA2Details;
